/**
 * ExportPanel — spec 010 export create/manage UI for the vehicle dossier.
 *
 * Renders an "Export service history" action in the dossier's action zone.
 * On click it expands a panel that walks through:
 *   1. manage  — list of existing exports + "Create new export" CTA.
 *   2. choose  — three full-width card-buttons (Minimal / Full / Public).
 *   3. confirm — FR-014 immutability warning + final Generate action.
 *                For "public": the pre-mint warning panel serves as confirm.
 *   4. polling — status indicator (pending → rendering → ready).
 *   5. done    — Download PDF / public link / revoke; "Back to exports" returns to manage.
 *
 * Phase model: idle → manage → choose → confirm → polling → done → manage
 *
 * verifyUrl contract: the URL is returned ONLY in the 202 create response and
 * is NEVER available from the server again. This component captures it in
 * local state immediately on mutation success and preserves it for the life
 * of the session. The user must copy or act on it before leaving the page.
 *
 * verifyUrl in the manage list: useListExports (GET /v1/vehicles/:id/exports)
 * does NOT return verifyUrl — it was never stored. Public export rows show
 * "Link shown only once at creation" copy and offer revoke + re-create only.
 *
 * Public link construction:
 *   verifyUrl  → https://<host>/verify/<token>
 *   public URL → <window.location.origin>/v/<token>
 *   Token is extracted from the tail of verifyUrl so the public URL always
 *   points to the current app origin (works in local dev, staging, prod).
 *
 * Revoke wiring:
 *   POST /v1/vehicles/:id/exports/:exportId/revoke → 200 { exportId, revoked: true }
 *   Owner-authed. If graydon's endpoint is not yet deployed (404 in staging),
 *   the button is present and the error is surfaced inline.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No navigator.onLine (uses useOnlineStatus()).
 *   - No localStorage/sessionStorage.
 *   - No Timestamp/FieldValue.
 *   - Download goes through downloadExport() — the one swap point post-wrap.
 *
 * Copy finalized 2026-06-25 (ogilvy pass).
 */

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/Spinner';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import {
  downloadExport,
  exportKeys,
  useCreateExport,
  useListExports,
  usePollExport,
  useRevokeExport,
} from '@/hooks/useExports';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import type { ApiError } from '@/lib/api';
import type { CreateExportResponse, ExportDisclosure, ExportSummary } from '@/types/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ChevronRight,
  Copy,
  Download,
  Eye,
  FileText,
  ShieldOff,
  WifiOff,
} from 'lucide-react';
import { useEffect, useId, useReducer, useRef, useState } from 'react';
import { PublicPhotosBlock } from './PublicPhotosBlock';

// ---------------------------------------------------------------------------
// FR-014 disclosure warning copy (PROVISIONAL — flag for ogilvy review).
//
// Functional spec: disclosure level is fixed at creation. Once the owner
// shares the verify link, the recipient sees exactly what was exported — the
// owner cannot retroactively reduce what's visible. "Full" adds the rendered
// service history document to the verify page; "Minimal" shows only the
// trust metadata (entry count, date, attestation status, SHA-256).
// ---------------------------------------------------------------------------

const DISCLOSURE_WARNING =
  'Once you share this link, you cannot reduce what the recipient can see. ' +
  'The disclosure level is locked when you create the link.';

// ---------------------------------------------------------------------------
// Pre-mint warning copy for public (redacted public dossier).
// ---------------------------------------------------------------------------

const PUBLIC_VERIFIED_SCOPE =
  'Absolute Pinks confirms only that these records have not been changed since the owner ' +
  'entered them. It does not confirm that the work was done, that the parts are genuine, ' +
  'or that the car is what the records say it is. Verify the vehicle yourself before you ' +
  'rely on it.';

const PUBLIC_RARE_CAR_WARNING =
  'Removing the VIN, owner details, and location does not make this page anonymous. For a ' +
  'rare or distinctive car, the chassis code, year, and service history together can still ' +
  'point to the exact vehicle.';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return iso;
  }
}

function actionableError(err: unknown): string {
  if (!err) return 'Something went wrong. Please try again.';
  const e = err as ApiError & { code?: string; message?: string };
  const code = e.code ?? '';
  const message = (e.message ?? '').toLowerCase();

  if (code === 'VALIDATION_ERROR' || message.includes('zero sealed')) {
    return 'No sealed service entries yet. Seal at least one entry before exporting.';
  }
  if (e instanceof Error && 'status' in e && (e as { status?: number }).status === 503) {
    return 'Export feature is not available right now. Contact support if this persists.';
  }
  if (e instanceof Error && 'status' in e && (e as { status?: number }).status === 403) {
    return 'You are not the owner of this vehicle.';
  }
  return e.message ?? 'Something went wrong. Please try again.';
}

/**
 * Extract the dossier token from a verifyUrl and build the public /v/:token URL.
 *
 * verifyUrl shape: https://<host>/verify/<token>
 * public URL:      <window.location.origin>/v/<token>
 *
 * Falls back to null when the token cannot be parsed so callers can guard.
 */
function buildPublicDossierUrl(verifyUrl: string): string | null {
  try {
    const url = new URL(verifyUrl);
    const parts = url.pathname.split('/');
    // pathname is /verify/<token> — token is the last non-empty segment.
    const token = parts.filter(Boolean).at(-1);
    if (!token) return null;
    return `${window.location.origin}/v/${token}`;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Sub-component: DisclosureChooser
// Replaces the old DisclosureToggle (radio-in-card). Each card IS a button
// that advances to the confirm phase on click. Full-width layout preserved.
// ---------------------------------------------------------------------------

interface DisclosureChooserProps {
  onSelect: (d: ExportDisclosure) => void;
  /** When returning from Back, this card is visually highlighted and focused. */
  lastDisclosure?: ExportDisclosure | null;
}

function DisclosureChooser({ onSelect, lastDisclosure }: DisclosureChooserProps) {
  const minimalRef = useRef<HTMLButtonElement>(null);
  const fullRef = useRef<HTMLButtonElement>(null);
  const publicRef = useRef<HTMLButtonElement>(null);

  // On mount: if returning via Back, focus the previously chosen card.
  // Runs only on mount — the component unmounts when leaving choose phase,
  // so this always targets the re-mount on Back navigation.
  useEffect(() => {
    if (lastDisclosure === 'minimal') minimalRef.current?.focus();
    else if (lastDisclosure === 'full') fullRef.current?.focus();
    else if (lastDisclosure === 'public') publicRef.current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function cardStyle(isHighlighted: boolean): React.CSSProperties {
    return {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: SPACE.sm,
      padding: SPACE.sm,
      border: `1px solid ${isHighlighted ? T.accent : T.border}`,
      borderRadius: RADIUS.md,
      background: isHighlighted ? `color-mix(in srgb, ${T.accent} 6%, ${T.card})` : T.card,
      cursor: 'pointer',
      width: '100%',
      textAlign: 'left',
      transition: 'border-color 150ms ease, background 150ms ease',
      fontFamily: F.body,
    };
  }

  function renderCard(
    d: ExportDisclosure,
    label: string,
    description: string,
    ref: React.RefObject<HTMLButtonElement | null>,
  ) {
    return (
      <button
        key={d}
        ref={ref}
        type="button"
        onClick={() => onSelect(d)}
        style={cardStyle(lastDisclosure === d)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: T.text,
              marginBottom: 2,
              fontFamily: F.body,
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: 13, color: T.text, lineHeight: 1.5, fontFamily: F.body }}>
            {description}
          </div>
        </div>
        <ChevronRight
          size={14}
          aria-hidden="true"
          style={{ flexShrink: 0, marginTop: 2, color: T.textMuted }}
        />
      </button>
    );
  }

  return (
    <div role="group" aria-label="Disclosure level">
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: T.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: SPACE.xs,
          fontFamily: F.body,
        }}
      >
        Disclosure
      </div>
      <div style={{ display: 'grid', gap: SPACE.xs }}>
        {renderCard(
          'minimal',
          'Minimal',
          'Trust metadata only: entry count, date, attestation status, and content hash. The service history document is not included.',
          minimalRef,
        )}
        {renderCard(
          'full',
          'Full',
          'Includes the rendered service history document on the verify page. Choose this for buyers or inspectors who need the full record.',
          fullRef,
        )}
        {renderCard(
          'public',
          'Redacted public dossier',
          'A public page with the VIN, owner details, and location removed. Anyone with the link can open it.',
          publicRef,
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ConfirmStep — shown for minimal/full before generating.
// Public uses PreMintWarningPanel instead.
// ---------------------------------------------------------------------------

interface ConfirmStepProps {
  disclosure: 'minimal' | 'full';
  includeBeforeAfterPhotos: boolean;
  onIncludeBeforeAfterPhotosChange: (value: boolean) => void;
  onConfirm: () => void;
  onBack: () => void;
  loading?: boolean;
  disabled?: boolean;
  error?: string | null;
}

function ConfirmStep({
  disclosure,
  includeBeforeAfterPhotos,
  onIncludeBeforeAfterPhotosChange,
  onConfirm,
  onBack,
  loading,
  disabled,
  error,
}: ConfirmStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const photosCheckboxId = useId();

  // Move focus to the step heading on mount (confirm phase entry).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const disclosureLabel = disclosure === 'minimal' ? 'Minimal' : 'Full';

  return (
    <div style={{ display: 'grid', gap: SPACE.md }} aria-live="polite">
      <h3
        ref={headingRef}
        tabIndex={-1}
        style={{
          margin: 0,
          fontSize: 13,
          fontWeight: 600,
          color: T.text,
          fontFamily: F.body,
          outline: 'none',
        }}
      >
        Confirm {disclosureLabel} export
      </h3>

      {/* FR-014 immutability warning */}
      <p
        style={{
          margin: 0,
          fontSize: 13,
          color: T.text,
          lineHeight: 1.6,
          fontFamily: F.body,
          padding: `${SPACE.xs}px ${SPACE.sm}px`,
          borderLeft: `3px solid ${T.orange}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: SPACE.xs,
        }}
      >
        <AlertTriangle
          size={14}
          aria-hidden="true"
          style={{ color: T.orange, flexShrink: 0, marginTop: 2 }}
        />
        {DISCLOSURE_WARNING}
      </p>

      {/* Owner opt-in: include before/after photos in the rendered PDF */}
      <label
        htmlFor={photosCheckboxId}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: SPACE.sm,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 12,
          color: T.text,
          lineHeight: 1.5,
          fontFamily: F.body,
        }}
      >
        <input
          id={photosCheckboxId}
          type="checkbox"
          checked={includeBeforeAfterPhotos}
          onChange={(e) => onIncludeBeforeAfterPhotosChange(e.currentTarget.checked)}
          disabled={disabled}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        Include before &amp; after photos in the PDF
      </label>

      {error ? (
        <p role="alert" style={{ margin: 0, fontSize: 13, color: T.red }}>
          {error}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          size="md"
          loading={loading ?? false}
          disabled={disabled ?? false}
          onClick={onConfirm}
        >
          Generate export
        </Button>
        <Button variant="secondary" size="md" disabled={loading ?? false} onClick={onBack}>
          Back
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: PreMintWarningPanel
// Shown when disclosure === 'public' as the confirm step.
// ---------------------------------------------------------------------------

interface PreMintWarningPanelProps {
  vehicleId: string;
  onConfirm: () => void;
  onBack: () => void;
  confirmed: boolean;
  setConfirmed: (v: boolean) => void;
  disabled?: boolean;
  confirmId: string;
}

function PreMintWarningPanel({
  vehicleId,
  onConfirm,
  onBack,
  confirmed,
  setConfirmed,
  disabled,
  confirmId,
}: PreMintWarningPanelProps) {
  const warningBlock = (icon: React.ReactNode, text: string, testId?: string): React.ReactNode => (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        gap: SPACE.sm,
        padding: SPACE.sm,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.md,
        background: T.card,
      }}
    >
      <div style={{ flexShrink: 0, marginTop: 1, color: T.textMuted }}>{icon}</div>
      <p style={{ margin: 0, fontSize: 12, color: T.text, lineHeight: 1.6, fontFamily: F.body }}>
        {text}
      </p>
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: SPACE.md }} aria-live="polite">
      {/* Section header */}
      <div>
        <h3
          style={{
            margin: `0 0 ${SPACE.xs}px`,
            fontSize: 13,
            fontWeight: 600,
            color: T.text,
            fontFamily: F.body,
          }}
        >
          Before you create a public link
        </h3>
        <p style={{ margin: 0, fontSize: 12, color: T.textMuted, lineHeight: 1.6 }}>
          Three things to check before this record goes public.
        </p>
      </div>

      {/* Warning 1 — what "verified" actually means */}
      {warningBlock(
        <AlertTriangle size={14} aria-hidden="true" />,
        PUBLIC_VERIFIED_SCOPE,
        'pre-mint-verified-scope',
      )}

      {/* Warning 2 — rare-car fingerprinting */}
      {warningBlock(
        <AlertTriangle size={14} aria-hidden="true" />,
        PUBLIC_RARE_CAR_WARNING,
        'pre-mint-rare-car',
      )}

      {/* Warning 3 — in-flow public photos review (replaces static advisory) */}
      <PublicPhotosBlock vehicleId={vehicleId} />

      {/* Acknowledgement checkbox */}
      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: SPACE.sm,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 12,
          color: T.text,
          lineHeight: 1.5,
          fontFamily: F.body,
        }}
      >
        <input
          id={confirmId}
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.currentTarget.checked)}
          disabled={disabled}
          style={{ marginTop: 2, flexShrink: 0 }}
          aria-label="I have read and understood the warnings above"
        />
        I have read the above and understand what this link will show to anyone who opens it.
      </label>

      {/* Actions */}
      <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <Button variant="primary" size="md" disabled={!confirmed || disabled} onClick={onConfirm}>
          Create public link
        </Button>
        <Button variant="secondary" size="md" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: StatusIndicator — renders during pending/rendering phase
// ---------------------------------------------------------------------------

function StatusIndicator({ status }: { status: ExportSummary['status'] }) {
  const label = status === 'rendering' ? 'Rendering PDF…' : 'Queued…';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACE.sm,
        padding: `${SPACE.sm}px 0`,
        color: T.textMuted,
        fontSize: 13,
        fontFamily: F.body,
      }}
      role="status"
      aria-live="polite"
    >
      <Spinner size={16} label={label} style={{ margin: 0 }} />
      {label}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: RevokeControl — shown on live public exports; revokes on confirm.
// Used both in PublicReadyPanel (done phase) and ExportRow (manage list).
// ---------------------------------------------------------------------------

interface RevokeControlProps {
  vehicleId: string;
  exportId: string;
  onRevoked: () => void;
}

function RevokeControl({ vehicleId, exportId, onRevoked }: RevokeControlProps) {
  const [confirming, setConfirming] = useState(false);
  const revokeMutation = useRevokeExport();

  function handleRevoke() {
    revokeMutation.reset();
    revokeMutation.mutate(
      { vehicleId, exportId },
      {
        onSuccess: () => {
          onRevoked();
        },
      },
    );
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: SPACE.xs,
          background: 'transparent',
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.md,
          padding: `${SPACE.xs}px ${SPACE.sm}px`,
          fontSize: 12,
          fontWeight: 600,
          color: T.red,
          cursor: 'pointer',
          minHeight: 44,
          fontFamily: F.body,
          transition: 'border-color 150ms ease',
        }}
        aria-label="Revoke public link"
      >
        <ShieldOff size={13} aria-hidden="true" />
        Revoke public link
      </button>
    );
  }

  return (
    <div
      style={{
        padding: SPACE.sm,
        border: `1px solid ${T.red}`,
        borderRadius: RADIUS.md,
        background: `color-mix(in srgb, ${T.red} 5%, ${T.card})`,
        display: 'grid',
        gap: SPACE.sm,
      }}
      role="alertdialog"
      aria-label="Confirm revoke"
    >
      <p style={{ margin: 0, fontSize: 12, color: T.text, lineHeight: 1.6, fontFamily: F.body }}>
        This stops the link working for everyone who has it, straight away. You cannot undo this. To
        share again, you would create a new link.
      </p>

      {revokeMutation.error ? (
        <p role="alert" style={{ margin: 0, fontSize: 12, color: T.red }}>
          {actionableError(revokeMutation.error)}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: SPACE.sm, flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          size="sm"
          loading={revokeMutation.isPending}
          onClick={handleRevoke}
          style={{ background: T.red, borderColor: T.red }}
        >
          Yes, revoke now
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={revokeMutation.isPending}
          onClick={() => {
            setConfirming(false);
            revokeMutation.reset();
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ExportRow — one row in the manage list.
// ---------------------------------------------------------------------------

interface ExportRowProps {
  vehicleId: string;
  export_: ExportSummary;
  onRevokeSuccess: () => void;
}

function ExportRow({ vehicleId, export_: exp, onRevokeSuccess }: ExportRowProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const isDownloadable =
    exp.status === 'ready' &&
    !exp.expired &&
    (exp.disclosure === 'minimal' || exp.disclosure === 'full');

  const isLivePublic = exp.status === 'ready' && !exp.expired && exp.disclosure === 'public';

  function rowStatusLabel(): string {
    if (exp.expired) return 'Expired';
    switch (exp.status) {
      case 'ready':
        return exp.disclosure === 'public' ? 'Live' : 'Ready';
      case 'rendering':
        return 'Rendering';
      case 'pending':
        return 'Pending';
      case 'failed':
        return 'Failed';
      default:
        return exp.status;
    }
  }

  const disclosureLabels: Record<ExportDisclosure, string> = {
    minimal: 'Minimal',
    full: 'Full',
    public: 'Public',
  };

  async function handleDownload() {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadExport(vehicleId, exp.exportId);
    } catch (err) {
      setDownloadError(actionableError(err));
    } finally {
      setDownloading(false);
    }
  }

  const statusLabel = rowStatusLabel();

  function rowStatusColor(): string {
    if (exp.expired) return T.orange;
    switch (exp.status) {
      case 'ready':
        return T.green;
      case 'failed':
        return T.red;
      default:
        // pending, rendering
        return T.textMuted;
    }
  }

  return (
    <div
      style={{
        padding: SPACE.sm,
        border: `1px solid ${T.border}`,
        borderRadius: RADIUS.md,
        background: T.card,
        display: 'grid',
        gap: SPACE.xs,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: SPACE.sm,
          flexWrap: 'wrap',
        }}
      >
        {/* Row metadata */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.xs, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: T.text,
              fontFamily: F.body,
            }}
          >
            {disclosureLabels[exp.disclosure]}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: rowStatusColor(),
              fontFamily: F.body,
            }}
          >
            · {statusLabel}
          </span>
          {exp.entryCount !== undefined ? (
            <span style={{ fontSize: 12, color: T.textMuted, fontFamily: F.body }}>
              · {exp.entryCount} {exp.entryCount === 1 ? 'entry' : 'entries'}
              {exp.asOf ? ` as of ${formatDate(exp.asOf)}` : ''}
            </span>
          ) : null}
        </div>

        {/* Row actions */}
        <div style={{ display: 'flex', gap: SPACE.xs, flexShrink: 0, alignItems: 'center' }}>
          {isDownloadable ? (
            <Button
              variant="secondary"
              size="sm"
              loading={downloading}
              onClick={() => void handleDownload()}
              aria-label="Download PDF"
            >
              <Download size={12} aria-hidden="true" />
              Download PDF
            </Button>
          ) : null}

          {isLivePublic ? (
            <RevokeControl
              vehicleId={vehicleId}
              exportId={exp.exportId}
              onRevoked={onRevokeSuccess}
            />
          ) : null}
        </div>
      </div>

      {/* Expiry + once-only note for public */}
      <p style={{ margin: 0, fontSize: 11, color: T.textMuted, fontFamily: F.body }}>
        Expires {formatDate(exp.expiresAt)}
        {exp.disclosure === 'public' ? ' · Link shown only once at creation.' : ''}
      </p>

      {downloadError ? (
        <p role="alert" style={{ margin: 0, fontSize: 12, color: T.red }}>
          {downloadError}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ExportList — manage phase; renders existing exports.
// 404 from the list endpoint degrades to empty state (not an error).
// ---------------------------------------------------------------------------

interface ExportListProps {
  vehicleId: string;
  onCreateNew: () => void;
}

function ExportList({ vehicleId, onCreateNew }: ExportListProps) {
  const { data, loading, error } = useListExports(vehicleId);
  const queryClient = useQueryClient();

  const is404 =
    error !== null && 'status' in error && (error as { status?: number }).status === 404;
  const hasOtherError = error !== null && !is404;

  function handleRevokeSuccess() {
    void queryClient.invalidateQueries({ queryKey: exportKeys.list(vehicleId) });
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACE.sm,
          padding: `${SPACE.sm}px 0`,
          color: T.textMuted,
          fontSize: 13,
          fontFamily: F.body,
        }}
      >
        <Spinner size={16} label="Loading exports" style={{ margin: 0 }} />
        Loading exports…
      </div>
    );
  }

  const exports = data ?? [];

  return (
    <div style={{ display: 'grid', gap: SPACE.md }}>
      {hasOtherError ? (
        <p role="alert" style={{ margin: 0, fontSize: 12, color: T.red, fontFamily: F.body }}>
          {actionableError(error)}
        </p>
      ) : null}

      {exports.length === 0 ? (
        <div style={{ display: 'grid', gap: SPACE.sm }}>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: T.textMuted,
              lineHeight: 1.5,
              fontFamily: F.body,
            }}
          >
            No exports yet. Generate a verifiable PDF of the sealed service record to share with a
            buyer, insurer, or inspector.
          </p>
          <div>
            <Button variant="primary" size="md" onClick={onCreateNew}>
              <FileText size={14} aria-hidden="true" />
              Create new export
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: SPACE.sm }}>
          {exports.map((exp) => (
            <ExportRow
              key={exp.exportId}
              vehicleId={vehicleId}
              export_={exp}
              onRevokeSuccess={handleRevokeSuccess}
            />
          ))}
          <div>
            <Button variant="secondary" size="md" onClick={onCreateNew}>
              <FileText size={14} aria-hidden="true" />
              Create new export
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: PublicReadyPanel — shareable /v/:token link + revoke
// ---------------------------------------------------------------------------

interface PublicReadyPanelProps {
  vehicleId: string;
  exportSummary: ExportSummary;
  verifyUrl: string;
}

function PublicReadyPanel({ vehicleId, exportSummary, verifyUrl }: PublicReadyPanelProps) {
  const [copied, setCopied] = useState(false);
  const [revoked, setRevoked] = useState(false);

  const publicUrl = buildPublicDossierUrl(verifyUrl);

  async function handleCopy() {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available — the link is visible for manual copy.
    }
  }

  if (revoked) {
    return (
      <div
        style={{
          padding: SPACE.md,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.md,
          background: T.card,
          display: 'grid',
          gap: SPACE.xs,
        }}
        role="status"
      >
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 600,
            color: T.textMuted,
            fontFamily: F.body,
          }}
        >
          Public link revoked
        </p>
        <p style={{ margin: 0, fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
          The link no longer works. Create a new export if you want to share this record again.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: SPACE.md }}>
      {/* Ready status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs, flexWrap: 'wrap' }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: T.green,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Live
        </span>
        {exportSummary.entryCount !== undefined ? (
          <span style={{ fontSize: 12, color: T.textMuted }}>
            · {exportSummary.entryCount} entr{exportSummary.entryCount === 1 ? 'y' : 'ies'}
            {exportSummary.asOf ? ` as of ${formatDate(exportSummary.asOf)}` : ''}
          </span>
        ) : null}
      </div>

      {/* Public dossier link */}
      <div>
        <p
          style={{
            margin: `0 0 ${SPACE.xs}px`,
            fontSize: 12,
            color: T.textMuted,
            lineHeight: 1.5,
          }}
        >
          Anyone with this link can open the public page. Copy it now. This is the only time it is
          shown.
        </p>

        {publicUrl ? (
          <div style={{ display: 'flex', alignItems: 'stretch', gap: SPACE.xs }}>
            <input
              type="text"
              readOnly
              value={publicUrl}
              aria-label="Shareable public dossier link"
              style={{
                flex: 1,
                border: `1px solid ${T.border}`,
                borderRadius: RADIUS.md,
                padding: `${SPACE.xs}px ${SPACE.sm}px`,
                fontSize: 12,
                fontFamily: F.mono,
                background: T.card,
                color: T.text,
                minWidth: 0,
                outline: 'none',
              }}
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void handleCopy()}
              aria-label={copied ? 'Link copied' : 'Copy public dossier link'}
              style={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
                ...(copied
                  ? {
                      background: `color-mix(in srgb, ${T.green} 10%, ${T.card})`,
                      color: T.green,
                      borderColor: `color-mix(in srgb, ${T.green} 30%, ${T.border})`,
                    }
                  : {}),
              }}
            >
              <Copy size={13} aria-hidden="true" />
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        ) : (
          <p role="alert" style={{ margin: 0, fontSize: 12, color: T.red }}>
            Could not construct the public link from the server response.
          </p>
        )}

        {/* Preview link */}
        {publicUrl ? (
          <div
            style={{ marginTop: SPACE.xs, display: 'flex', alignItems: 'center', gap: SPACE.xs }}
          >
            <Eye size={12} color={T.textMuted} aria-hidden="true" />
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                color: T.textMuted,
                fontFamily: F.body,
                textDecoration: 'underline',
              }}
            >
              Preview what a stranger sees
            </a>
          </div>
        ) : null}

        <p style={{ margin: `${SPACE.xs}px 0 0`, fontSize: 11, color: T.textMuted }}>
          Expires {formatDate(exportSummary.expiresAt)}.
        </p>
      </div>

      {/* Revoke control */}
      <RevokeControl
        vehicleId={vehicleId}
        exportId={exportSummary.exportId}
        onRevoked={() => setRevoked(true)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ReadyPanel — download + verify link (minimal/full)
// ---------------------------------------------------------------------------

interface ReadyPanelProps {
  vehicleId: string;
  exportSummary: ExportSummary;
  verifyUrl: string;
}

function ReadyPanel({ vehicleId, exportSummary, verifyUrl }: ReadyPanelProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadExport(vehicleId, exportSummary.exportId);
    } catch (err) {
      setDownloadError(actionableError(err));
    } finally {
      setDownloading(false);
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available (rare in modern browsers on https) —
      // the link is visible in the field so the user can copy manually.
    }
  }

  return (
    <div style={{ display: 'grid', gap: SPACE.md }}>
      {/* Ready status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: T.green,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Ready
        </span>
        {exportSummary.entryCount !== undefined ? (
          <span style={{ fontSize: 12, color: T.textMuted }}>
            · {exportSummary.entryCount} entr{exportSummary.entryCount === 1 ? 'y' : 'ies'}
            {exportSummary.asOf ? ` as of ${formatDate(exportSummary.asOf)}` : ''}
          </span>
        ) : null}
      </div>

      {/* Download */}
      <Button
        variant="primary"
        size="md"
        loading={downloading}
        onClick={() => void handleDownload()}
      >
        <Download size={14} aria-hidden="true" />
        Download PDF
      </Button>

      {downloadError ? (
        <p role="alert" style={{ margin: 0, fontSize: 13, color: T.red }}>
          {downloadError}
        </p>
      ) : null}

      {/* Verify link */}
      <div>
        <p
          style={{
            margin: `0 0 ${SPACE.xs}px`,
            fontSize: 12,
            color: T.textMuted,
            lineHeight: 1.5,
          }}
        >
          Share this link. Anyone with it can verify the record on our public page. This is the only
          time this exact link is shown; copy it before leaving.
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'stretch',
            gap: SPACE.xs,
          }}
        >
          <input
            type="text"
            readOnly
            value={verifyUrl}
            aria-label="Shareable verify link"
            style={{
              flex: 1,
              border: `1px solid ${T.border}`,
              borderRadius: RADIUS.md,
              padding: `${SPACE.xs}px ${SPACE.sm}px`,
              fontSize: 12,
              fontFamily: F.mono,
              background: T.card,
              color: T.text,
              minWidth: 0,
              outline: 'none',
            }}
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleCopy()}
            aria-label={copied ? 'Link copied' : 'Copy verify link'}
            style={{
              whiteSpace: 'nowrap',
              flexShrink: 0,
              ...(copied
                ? {
                    background: `color-mix(in srgb, ${T.green} 10%, ${T.card})`,
                    color: T.green,
                    borderColor: `color-mix(in srgb, ${T.green} 30%, ${T.border})`,
                  }
                : {}),
            }}
          >
            <Copy size={13} aria-hidden="true" />
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p
          style={{
            margin: `${SPACE.xs}px 0 0`,
            fontSize: 11,
            color: T.textMuted,
          }}
        >
          Expires {formatDate(exportSummary.expiresAt)}.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase model + reducer
// ---------------------------------------------------------------------------

export interface ExportPanelProps {
  vehicleId: string;
}

type PanelPhase =
  | { kind: 'idle' }
  | { kind: 'manage' }
  | { kind: 'choose' }
  | { kind: 'confirm'; disclosure: ExportDisclosure }
  | { kind: 'polling'; exportId: string; verifyUrl: string; disclosure: ExportDisclosure }
  | { kind: 'done'; summary: ExportSummary; verifyUrl: string; disclosure: ExportDisclosure };

type PanelAction =
  | { type: 'open' }
  | { type: 'close' }
  | { type: 'new-export' }
  | { type: 'select-disclosure'; disclosure: ExportDisclosure }
  | { type: 'back-to-choose' }
  | { type: 'back-to-manage' }
  | { type: 'created'; exportId: string; verifyUrl: string; disclosure: ExportDisclosure }
  | { type: 'ready'; summary: ExportSummary }
  | { type: 'failed' };

function panelReducer(state: PanelPhase, action: PanelAction): PanelPhase {
  switch (action.type) {
    case 'open':
      return state.kind === 'idle' ? { kind: 'manage' } : state;
    case 'close':
      return { kind: 'idle' };
    case 'new-export':
      return state.kind === 'manage' ? { kind: 'choose' } : state;
    case 'select-disclosure':
      return state.kind === 'choose' ? { kind: 'confirm', disclosure: action.disclosure } : state;
    case 'back-to-choose':
      return state.kind === 'confirm' ? { kind: 'choose' } : state;
    case 'back-to-manage':
      return { kind: 'manage' };
    case 'created':
      return {
        kind: 'polling',
        exportId: action.exportId,
        verifyUrl: action.verifyUrl,
        disclosure: action.disclosure,
      };
    case 'ready':
      if (state.kind === 'polling') {
        return {
          kind: 'done',
          summary: action.summary,
          verifyUrl: state.verifyUrl,
          disclosure: state.disclosure,
        };
      }
      return state;
    case 'failed':
      return { kind: 'choose' };
    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// Main export: ExportPanel
// ---------------------------------------------------------------------------

export function ExportPanel({ vehicleId }: ExportPanelProps) {
  const online = useOnlineStatus();
  const groupId = useId();
  const confirmId = `${groupId}-confirm`;
  const queryClient = useQueryClient();

  const [phase, dispatch] = useReducer(panelReducer, { kind: 'idle' });
  const [disclosure, setDisclosure] = useState<ExportDisclosure>('minimal');
  const [lastDisclosure, setLastDisclosure] = useState<ExportDisclosure | null>(null);
  const [warningConfirmed, setWarningConfirmed] = useState(false);
  const [includeBeforeAfterPhotos, setIncludeBeforeAfterPhotos] = useState(false);

  const createMutation = useCreateExport();

  // Poll only while in the 'polling' phase.
  const pollVehicleId = phase.kind === 'polling' ? vehicleId : null;
  const pollExportId = phase.kind === 'polling' ? phase.exportId : null;
  const { data: pollData, error: pollError } = usePollExport(pollVehicleId, pollExportId);

  // Transition out of polling when the export reaches a terminal state.
  if (phase.kind === 'polling') {
    if (pollData?.status === 'ready') {
      dispatch({ type: 'ready', summary: pollData });
    } else if (pollData?.status === 'failed' || pollError) {
      dispatch({ type: 'failed' });
    }
  }

  function handleSelectDisclosure(d: ExportDisclosure) {
    setDisclosure(d);
    setLastDisclosure(d);
    setWarningConfirmed(false);
    setIncludeBeforeAfterPhotos(false);
    createMutation.reset();
    dispatch({ type: 'select-disclosure', disclosure: d });
  }

  function handleCreate() {
    createMutation.reset();
    createMutation.mutate(
      {
        vehicleId,
        request: {
          artifactType: 'serviceHistory',
          disclosure,
          ...(includeBeforeAfterPhotos ? { includeBeforeAfterPhotos: true } : {}),
        },
      },
      {
        onSuccess: (data: CreateExportResponse) => {
          void queryClient.invalidateQueries({ queryKey: exportKeys.list(vehicleId) });
          dispatch({
            type: 'created',
            exportId: data.exportId,
            verifyUrl: data.verifyUrl,
            disclosure: data.disclosure,
          });
        },
      },
    );
  }

  function handleBack() {
    createMutation.reset();
    dispatch({ type: 'back-to-choose' });
  }

  // ---------------------------------------------------------------------------
  // Render: idle — just the trigger button
  // ---------------------------------------------------------------------------

  if (phase.kind === 'idle') {
    return (
      <div style={{ marginTop: SPACE.xl }}>
        <Button
          variant="secondary"
          size="md"
          onClick={() => dispatch({ type: 'open' })}
          style={{ borderColor: T.border }}
        >
          <FileText size={14} aria-hidden="true" />
          Export service history
        </Button>
        <p
          style={{
            marginTop: SPACE.xs,
            fontSize: 12,
            color: T.textMuted,
            fontFamily: F.body,
            lineHeight: 1.5,
          }}
        >
          Generate a verifiable PDF of the sealed service record to share with a buyer, insurer, or
          inspector.
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: expanded panel (manage / choose / confirm / polling / done)
  // ---------------------------------------------------------------------------

  const isCreating = createMutation.isPending;
  const isPolling = phase.kind === 'polling';
  const createError = createMutation.error ? actionableError(createMutation.error) : null;

  return (
    <Card style={{ marginTop: SPACE.xl }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: SPACE.md,
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: F.body,
            color: T.text,
          }}
        >
          Export service history
        </h2>
        <button
          type="button"
          onClick={() => dispatch({ type: 'close' })}
          aria-label="Close export panel"
          style={{
            background: 'transparent',
            border: 'none',
            color: T.textMuted,
            fontSize: 18,
            cursor: 'pointer',
            padding: `${SPACE.xs}px`,
            lineHeight: 1,
            minWidth: 44,
            minHeight: 44,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          &times;
        </button>
      </div>

      {/* Offline guard */}
      {!online ? (
        <p
          role="alert"
          style={{
            fontSize: 13,
            color: T.orange,
            margin: `0 0 ${SPACE.md}px`,
            lineHeight: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: SPACE.xs,
          }}
        >
          <WifiOff size={14} aria-hidden="true" style={{ flexShrink: 0 }} />
          Export requires a connection. Connect and try again.
        </p>
      ) : null}

      {/* Phase: manage — existing exports list */}
      {phase.kind === 'manage' && (
        <ExportList vehicleId={vehicleId} onCreateNew={() => dispatch({ type: 'new-export' })} />
      )}

      {/* Phase: choose — disclosure card-buttons */}
      {phase.kind === 'choose' && (
        <div style={{ display: 'grid', gap: SPACE.md }}>
          <DisclosureChooser onSelect={handleSelectDisclosure} lastDisclosure={lastDisclosure} />
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => dispatch({ type: 'back-to-manage' })}
            >
              ← Back to exports
            </Button>
          </div>
        </div>
      )}

      {/* Phase: confirm — minimal/full */}
      {phase.kind === 'confirm' && phase.disclosure !== 'public' && (
        <ConfirmStep
          disclosure={phase.disclosure}
          includeBeforeAfterPhotos={includeBeforeAfterPhotos}
          onIncludeBeforeAfterPhotosChange={setIncludeBeforeAfterPhotos}
          onConfirm={handleCreate}
          onBack={handleBack}
          loading={isCreating}
          disabled={!online}
          error={createError}
        />
      )}

      {/* Phase: confirm — public (pre-mint warning panel as confirm body) */}
      {phase.kind === 'confirm' && phase.disclosure === 'public' && (
        <PreMintWarningPanel
          vehicleId={vehicleId}
          onConfirm={handleCreate}
          onBack={handleBack}
          confirmed={warningConfirmed}
          setConfirmed={setWarningConfirmed}
          disabled={isCreating || !online}
          confirmId={confirmId}
        />
      )}

      {/* Phase: polling */}
      {isPolling && <StatusIndicator status={pollData?.status ?? 'pending'} />}

      {/* Phase: done */}
      {phase.kind === 'done' ? (
        <div style={{ display: 'grid', gap: SPACE.md }}>
          {phase.disclosure === 'public' ? (
            <PublicReadyPanel
              vehicleId={vehicleId}
              exportSummary={phase.summary}
              verifyUrl={phase.verifyUrl}
            />
          ) : (
            <ReadyPanel
              vehicleId={vehicleId}
              exportSummary={phase.summary}
              verifyUrl={phase.verifyUrl}
            />
          )}
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void queryClient.invalidateQueries({ queryKey: exportKeys.list(vehicleId) });
                dispatch({ type: 'back-to-manage' });
              }}
            >
              ← Back to exports
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
